import React, { useEffect } from "react";
import { connect, batch } from "react-redux";
import { bindActionCreators } from "redux";
import classNames from "classnames";
import { createSelector } from "reselect";

// Actions
import {
  fetchSetLocalStudy,
  fetchSetRemoteStudy,
  setView,
  errorResponse,
  toggleLock,
  toggleLoadDialog,
  fetchSetLocalStudyTemplate,
  fetchSetRemoteStudyTemplate,
  addStudyInfo,
  setTemplatePrompt,
  receiveTemplate,
  resetTemplate,
  resetSetStudy,
  setLocalFilepath,
  fetchLocalStudiesList,
  fetchRemoteStudiesList,
} from "../../actions";

// MUI Components
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Backdrop from "@material-ui/core/Backdrop";
import Box from "@material-ui/core/Box";
import InputBase from "@material-ui/core/InputBase";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
//Icons
import ComputerIcon from "@material-ui/icons/Computer";
import FolderIcon from "@material-ui/icons/Folder";
import CloudIcon from "@material-ui/icons/Cloud";

// Other Files
import { colors } from "../../constants/colors";
import { viewIds } from "../../constants/componentMap";
import { wifiEnabled, remoteStatus } from "../../constants/constants";
import Header from "../Header/Header";
import SnackbarInfo from "../Shared/Snackbar";
import StudyDataUnavailableDialog from "./StudyDataUnavailableDialog";
import LoadStudyConflictDialog from "./LoadStudyConflictDialog";
import TemplateDialog from "./TemplateDialog";
import Dropzone from "../Shared/Dropzone";
import StudyDataTable from "./StudyDataTable";
import { generateStudyIdAndTimeStamp } from "../../constants/utilityFunctions";
import { customTheme } from "../../constants/customTheme";

const useStyles = makeStyles({
  fill: {
    flex: "1 0 auto",
    backgroundColor: (props) => colors[props.theme].background,
  },
  cardRoot: (props) => ({
    ...customTheme(props.theme).card.root,
    padding: "0px",
    width: "calc(100% - 60px)",
    height: "calc(100% - 60px)",
    maxWidth: "calc(100% - 60px)",
    maxHeight: "calc(100% - 60px)",
    "@media only screen and (max-height: 600px)": {
      width: "calc(100% - 50px)",
      height: "calc(100% - 60px)",
      maxWidth: "calc(100% - 50px)",
      maxHeight: "calc(100% - 60px)",
    },
  }),
  copyright: customTheme().copyright,
  loadingStudy: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
  },
  centerText: {
    display: "flex",
    justifyContent: "center",
    marginTop: "100px",
    flexDirection: "column",
    alignItems: "center",
  },
  backdrop: {
    zIndex: 2,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
  },
  backdropText: {
    width: "350px",
    paddingTop: "15px",
    textAlign: "center",
    fontSize: "1.2rem",
  },
  progressText: {
    width: "350px",
    paddingTop: "15px",
    textAlign: "center",
  },
  appBar: {
    backgroundColor: (props) => colors[props.theme].headerBkg,
    "& .MuiTabs-root": {
      height: "40px",
    },
  },
  tab: {
    flexBasis: "unset",
    flexShrink: "unset",
    minHeight: "unset",
    "& .MuiTab-wrapper": {
      ...customTheme().tabTitle,
      fontSize: "1.0rem",
      flexDirection: "unset",
      textTransform: "capitalize",
      "& .MuiSvgIcon-root": {
        marginRight: "10px",
        marginBottom: "0px",
      },
    },
  },
  dropdownForm: {
    marginLeft: "20px",
    width: "225px",
  },
  select: {
    marginRight: "0px",
  },
  fileIcon: (props) => customTheme(props.theme).iconButton,
  localFolderpathText: {
    padding: "0px",
    width: "50%",
  },
  formText: customTheme().formText,
  dropzone: {
    height: "100%",
  },
  progressCircle: {
    "& .MuiCircularProgress-static": {
      transition: "transform 0ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    },
    "& .MuiCircularProgress-circleStatic": {
      transition: "stroke-dashoffset 0ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    },
  },
  progressLabel: {
    fontSize: "0.9rem",
  },
  subDiv: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: "10px",
    marginBottom: "10px",
  },
});

const LoadStudy = (props) => {
  const classes = useStyles(props);

  // Hook States
  const [tab, setTab] = React.useState("LOCAL");
  const [templateDialog, setTemplateDialog] = React.useState({
    open: false,
    templateInfo: { participantId: "", protocolId: "", protocolName: "" },
  });
  const [loadStudyConflictDialog, setLoadStudyConflictDialog] = React.useState({
    open: false,
    study: {},
    prompt: null,
    remoteHashComparison: {},
  });
  const [progressIndicator, setProgressIndicator] = React.useState({
    value: 0,
    message: null,
  }); // Circular progress value

  useEffect(() => {
    if (props.templateReceieved === true) {
      setTemplateDialog({
        ...templateDialog,
        open: true,
        templateInfo: {
          ...templateDialog.templateInfo,
          id: props.templateReceivedInfo.id,
          startedAt: props.templateReceivedInfo.startedAt,
          studyDate: props.templateReceivedInfo.studyDate,
          participantId: props.templateReceivedInfo.participantId,
          skinType: props.templateReceivedInfo.skinType,
          gender: props.templateReceivedInfo.gender,
          otherId: props.templateReceivedInfo.otherId,
          birthYear: props.templateReceivedInfo.birthYear,
          protocolId: props.templateReceivedInfo.protocolId,
          protocolName: props.templateReceivedInfo.protocolName,
        },
      });
    }
  }, [props.templateReceieved]);

  // useEffect(() => {
  //   props.eventSource.addEventListener("progress_indicator", handleEvent);
  //   return () => {
  //     props.eventSource.removeEventListener("progress_indicator", handleEvent);
  //   };
  // }, []);

  const handleEvent = (e) => {
    switch (e.type) {
      case "progress_indicator":
        let data = JSON.parse(e.data);
        console.log(data);
        setProgressIndicator({
          value: data.value * 100,
          message: data.message,
        });
        break;
      default:
    }
  };

  function handleLocalFileClickRow(rowId) {
    let selectedLocalStudy = props.localStudyInfoTableData.filter(
      (studyEntry) => {
        return studyEntry.id === rowId;
      }
    )[0];
    if (Object.keys(selectedLocalStudy.multiple_study_files).length !== 0) {
      setLoadStudyConflictDialog({
        ...loadStudyConflictDialog,
        open: true,
        prompt: "LOCAL",
        study: {
          [selectedLocalStudy.id]: selectedLocalStudy.multiple_study_files,
        },
      });
    } else {
      handleGetLocalStudy(selectedLocalStudy.id);
    }
  }

  function handleDropzoneUpload(filepath) {
    // setProgressIndicator({ value: 0, message: null });
    // let seperator;
    // if (filepath.includes("\\")) {
    //   seperator = "\\";
    // } else {
    //   seperator = "/";
    // }
    // const pathArray = filepath.split(seperator);
    // const oldPath = pathArray.splice(0, pathArray.length - 1).join(seperator);
    // const remote = window.require("electron").remote;
    // const electronFs = remote.require("fs");
    // let allFilesInFolder = {};
    // electronFs.readdirSync(oldPath).forEach((file) => {
    //   if (file.endsWith(".study") || file.endsWith(".study.encrypt")) {
    //     allFilesInFolder[file] = { filepath: oldPath + seperator + file };
    //   }
    // });
    // let studyName = pathArray[0].split(".")[0];
    // if (Object.keys(allFilesInFolder).length === 1) {
    //   handleGetLocalStudy(filepath);
    // } else {
    //   setLoadStudyConflictDialog({
    //     ...loadStudyConflictDialog,
    //     open: true,
    //     prompt: "LOCAL",
    //     study: { [studyName]: allFilesInFolder },
    //   });
    // }
  }

  function handleGetLocalStudy(studyFilepath) {
    setProgressIndicator({ value: 0, message: null });
    if (props.templatePrompt) {
      let newTemplateStudyInfo = handleNewTemplateStudyInfo();
      props.fetchSetLocalStudyTemplate(studyFilepath, newTemplateStudyInfo);
    } else {
      props.fetchSetLocalStudy(studyFilepath);
    }
  }

  function handleRemoteFileClickRow(rowId) {
    let selectedRemoteStudy = props.remoteStudyInfoTableData.filter(
      (studyEntry) => {
        return studyEntry.id === rowId;
      }
    )[0];

    // Local copy with a different study Hash
    if (
      ![remoteStatus.sync, remoteStatus.match].includes(
        selectedRemoteStudy.status
      )
    ) {
      let localCopy = props.localStudyInfoTableData.filter((studyEntry) => {
        return studyEntry.id === rowId;
      });
      setLoadStudyConflictDialog({
        ...loadStudyConflictDialog,
        open: true,
        prompt: "CLOUD",
        study: selectedRemoteStudy,
        remoteHashComparison: {
          localCopy: localCopy[0],
          status: selectedRemoteStudy.status,
        },
      });
    } else {
      handleGetRemoteStudy(selectedRemoteStudy.id);
    }
  }

  function handleGetRemoteStudy(studyId) {
    setProgressIndicator({ value: 0, message: null });
    if (props.templatePrompt) {
      let newTemplateStudyInfo = handleNewTemplateStudyInfo();
      props.fetchSetRemoteStudyTemplate(studyId, newTemplateStudyInfo);
    } else {
      props.fetchSetRemoteStudy(studyId);
    }
  }

  function handleTabChange(event, newValue) {
    setProgressIndicator({ value: 0, message: null });
    if (newValue === "CLOUD" && !props.remoteStudiesReceived) {
      props.fetchRemoteStudiesList();
    }
    setTab(newValue);
  }

  // TEMPLATE
  function handleNewTemplateStudyInfo() {
    let studyStamps = generateStudyIdAndTimeStamp();
    return {
      startedAt: studyStamps.startedAt,
      studyDate: studyStamps.studyDate,
      id: studyStamps.id,
    };
  }

  function handleTemplateStudyStart(values) {
    // Start a new study using the template study
    if (props.templateReceieved) {
      batch(() => {
        props.receiveTemplate(false, null);
        props.addStudyInfo(values, values.id); // add studyInfo to the redux state tree
        props.setView(viewIds.mainApp);
      });
    }
  }

  function handleTemplateCancel() {
    props.receiveTemplate(false, null);
    setTemplateDialog({ ...templateDialog, open: false });
    props.resetTemplate();
    setProgressIndicator({ value: 0, message: null });
  }

  // STUDY DATA UNAVAILABLE DIALOG
  function handleLockUnlocked() {
    batch(() => {
      props.toggleLock();
      props.toggleLoadDialog();
      props.setView(viewIds.mainApp);
    });
  }

  function handleLoadStudyLockedCancel() {
    props.toggleLoadDialog();
    props.resetSetStudy();
    setProgressIndicator({ value: 0, message: null });
  }

  // CHANGE LOCAL DIRECTORY
  const handleLocalFilepathChange = (newLocalFilepath) => {
    props.setLocalFilepath(newLocalFilepath);
    props.fetchLocalStudiesList();
  };
  const handleFileExplorer = () => {
    // const { dialog } = window.require("electron").remote;
    // const defaultPath = props.localFolderpath.split("/").join("\\");
    // dialog.showOpenDialog(
    //   {
    //     defaultPath: defaultPath,
    //     properties: ["openDirectory"],
    //   },
    //   (filePaths) => {
    //     if (filePaths !== undefined && filePaths.length !== 0) {
    //       let newPath = filePaths[0].split("\\").join("/");
    //       handleLocalFilepathChange(newPath);
    //     }
    //   }
    // );
  };

  const DropdownSelection = () => {
    return (
      <FormControl className={classes.dropdownForm}>
        <Select
          className={classes.select}
          onChange={(event) => props.setTemplatePrompt(event.target.value)}
          value={props.templatePrompt}
        >
          <MenuItem value={true}>
            Start follow-up study using previous study as the protocol
          </MenuItem>
          <MenuItem value={false}>Load study with data</MenuItem>
        </Select>
      </FormControl>
    );
  };

  return (
    <Grid
      item
      container
      className={classNames(classes.fill, "noselect")}
      direction="column"
    >
      {/* HEADER */}
      <Header
      // eventSource={props.eventSource}
      />

      <Grid
        item
        container
        className={classes.fill}
        direction="column"
        justify="space-between"
      >
        <Grid
          item
          container
          className={classes.fill}
          direction="column"
          justify="center"
          alignItems="center"
        >
          {/* INFORMATION */}
          <Card className={classes.cardRoot}>
            <AppBar
              color="default"
              className={classes.appBar}
              position="static"
            >
              <Tabs
                value={tab}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                onChange={handleTabChange}
              >
                <Tab
                  label="Local Folder"
                  value="LOCAL"
                  icon={<FolderIcon />}
                  className={classes.tab}
                />

                {wifiEnabled && (
                  <Tab
                    label="Cloud"
                    value="CLOUD"
                    disabled={true}
                    icon={<CloudIcon />}
                    className={classes.tab}
                  />
                )}
                <Tab
                  label="Browse"
                  value="BROWSE"
                  disabled={true}
                  icon={<ComputerIcon />}
                  className={classes.tab}
                />
              </Tabs>
            </AppBar>
            {tab === "LOCAL" ? (
              /////// LOCAL FOLDER //////////
              props.localStudiesFetching ? (
                <div className={classes.centerText}>
                  <CircularProgress size={35} />
                  <Typography className={classes.progressText}>
                    {progressIndicator.message !== null
                      ? progressIndicator.message
                      : "Loading studies..."}
                  </Typography>
                </div>
              ) : (
                <div>
                  <div className={classes.subDiv}>
                    <DropdownSelection />
                    <InputBase
                      className={classes.localFolderpathText}
                      value={props.localFolderpath}
                      multiline
                      rowsMax={2}
                      readOnly
                      startAdornment={
                        <Tooltip
                          placement="top"
                          title="Change Directory"
                          enterTouchDelay={300}
                          classes={{
                            tooltip: classes.tooltip,
                          }}
                        >
                          <InputAdornment position="start">
                            <IconButton
                              className={classes.fileIcon}
                              disabled={true}
                              onClick={handleFileExplorer}
                            >
                              <FolderIcon />
                            </IconButton>
                          </InputAdornment>
                        </Tooltip>
                      }
                      inputProps={{
                        className: classes.formText,
                      }}
                    />
                  </div>
                  <StudyDataTable
                    dataTablePrompt={"LOCAL"}
                    handleClickRow={(rowData) => {
                      handleLocalFileClickRow(rowData);
                    }}
                    data={props.localStudyInfoTableData}
                  />
                </div>
              )
            ) : tab === "BROWSE" ? (
              /////// BROWSE //////////
              <div className={classes.dropzone}>
                <div className={classes.subDiv}>
                  <DropdownSelection />
                </div>
                <Dropzone
                  formatError={(message) => props.errorResponse(message)}
                  upload={handleDropzoneUpload}
                  acceptedFormat={[".study", ".study.encrypt"]}
                />
              </div>
            ) : /////// CLOUD //////////
            props.remoteStudiesFetching ? (
              <div className={classes.centerText}>
                <CircularProgress size={35} />
                <Typography className={classes.progressText}>
                  {progressIndicator.message !== null
                    ? progressIndicator.message
                    : "Loading studies..."}
                </Typography>
              </div>
            ) : (
              <div>
                <div className={classes.subDiv}>
                  <DropdownSelection />
                </div>
                <StudyDataTable
                  dataTablePrompt={"REMOTE"}
                  handleClickRow={(rowData) => {
                    handleRemoteFileClickRow(rowData);
                  }}
                  data={props.remoteStudyInfoTableData}
                />
              </div>
            )}
          </Card>
        </Grid>

        <Backdrop
          className={classes.backdrop}
          open={props.setStudyFetching || props.templateFetching}
        >
          <div className={classes.loadingStudy}>
            {progressIndicator.value === 0 ? (
              <CircularProgress size={35} />
            ) : (
              <Box
                className={classes.progressCircle}
                position="relative"
                display="inline-flex"
              >
                <CircularProgress
                  variant="static"
                  value={progressIndicator.value}
                  className={classes.circle}
                  size={50}
                />
                <Box
                  top={0}
                  left={0}
                  bottom={0}
                  right={0}
                  position="absolute"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography
                    className={classes.progressLabel}
                    variant="caption"
                    component="div"
                  >{`${Math.round(progressIndicator.value)}%`}</Typography>
                </Box>
              </Box>
            )}
            <Typography className={classes.backdropText}>
              {progressIndicator.message !== null
                ? progressIndicator.message
                : "Loading study..."}
            </Typography>
          </div>
        </Backdrop>
        {props.studyLoadDialog && (
          <StudyDataUnavailableDialog
            open={props.studyLoadDialog}
            handleLockUnlocked={handleLockUnlocked}
            onClose={handleLoadStudyLockedCancel}
            theme={props.theme}
          />
        )}
        {templateDialog.open && (
          <TemplateDialog
            onClose={() =>
              setTemplateDialog({ ...templateDialog, open: false })
            }
            open={templateDialog.open}
            info={templateDialog.templateInfo}
            submit={handleTemplateStudyStart}
            cancel={handleTemplateCancel}
            theme={props.theme}
          />
        )}

        {loadStudyConflictDialog.open && (
          <LoadStudyConflictDialog
            onClose={() =>
              setLoadStudyConflictDialog({
                ...loadStudyConflictDialog,
                open: false,
              })
            }
            open={loadStudyConflictDialog.open}
            study={loadStudyConflictDialog.study}
            prompt={loadStudyConflictDialog.prompt}
            remoteHashComparison={loadStudyConflictDialog.remoteHashComparison}
            handleGetRemoteStudy={(studyId) => handleGetRemoteStudy(studyId)}
            handleGetLocalStudy={(studyFilepath) =>
              handleGetLocalStudy(studyFilepath)
            }
            theme={props.theme}
          />
        )}

        {/* COPYRIGHT INFO */}
        <Grid item align="center" className={classes.copyright}>
          <Typography variant="caption" gutterBottom>
            This software is for investigational research use only. It is
            provided as-is with no warranty. &copy;{new Date().getFullYear()}{" "}
            MicroElastic Ultrasound Systems
          </Typography>
        </Grid>
      </Grid>

      {/* SNACKBAR FOR MESSAGE DISPLAY */}
      <SnackbarInfo />
    </Grid>
  );
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      fetchSetLocalStudy,
      fetchSetRemoteStudy,
      setView,
      errorResponse,
      toggleLock,
      toggleLoadDialog,
      fetchSetLocalStudyTemplate,
      fetchSetRemoteStudyTemplate,
      addStudyInfo,
      setTemplatePrompt,
      receiveTemplate,
      resetTemplate,
      resetSetStudy,
      setLocalFilepath,
      fetchLocalStudiesList,
      fetchRemoteStudiesList,
    },
    dispatch
  );
};

// this uses the reselect library
// memoizes the (expensive) filter calculation and only updates it when one of its input variables changes
const reshapeLocalStudies = createSelector(
  [(state) => state.availableStudies.localStudyInfoById],
  (localStudyInfoById) => {
    return Object.keys(localStudyInfoById).map((studyId) => {
      let study = localStudyInfoById[studyId];
      return {
        id: studyId,
        participantId: study.participantId,
        protocolName: study.protocolName,
        username: study.username,
        progress: study.progress === null ? "NA" : study.progress + "%",
        studyDate: study.studyDate,
        study_filepath: study.study_filepath,
        multiple_study_files: study.multiple_study_files,
        studyHash: study.studyHash ? study.studyHash : [],
      };
    });
  }
);

const reshapeRemoteStudies = createSelector(
  [
    (state) => state.availableStudies.remoteStudyInfoById,
    (state) => state.availableStudies.localStudyInfoById,
  ],
  (remoteStudyInfoById, localStudyInfoById) => {
    return Object.keys(remoteStudyInfoById).map((studyId) => {
      let study = remoteStudyInfoById[studyId];
      let status;

      if (Object.keys(localStudyInfoById).includes(studyId)) {
        let localCopy = localStudyInfoById[studyId];
        if (
          localCopy.studyHash &&
          study.studyHash &&
          localCopy.studyHash.length !== 0 &&
          study.studyHash.length !== 0
        ) {
          let remoteHash = study.studyHash.map((hash) => {
            return Object.keys(hash)[0];
          });
          let localHash = localCopy.studyHash.map((hash) => {
            return Object.keys(hash)[0];
          });

          if (remoteHash[0] === localHash[0]) {
            status = remoteStatus.match;
          } else if (localHash.includes(remoteHash[0])) {
            status = remoteStatus.localVersion;
          } else if (remoteHash.includes(localHash[0])) {
            status = remoteStatus.remoteVersion;
          } else {
            status = remoteStatus.divergent;
          }
        } else {
          status = remoteStatus.divergent;
        }
      } else {
        status = remoteStatus.sync;
      }

      return {
        id: studyId,
        participantId: study.participantId,
        protocolName: study.protocolName,
        username: study.username,
        progress: study.progress === null ? "NA" : study.progress + "%",
        studyDate: study.studyDate,
        studyHash: study.studyHash ? study.studyHash : [],
        status: status,
      };
    });
  }
);

const mapStateToProps = (state) => {
  return {
    localStudiesFetching: state.availableStudies.localStudiesFetching,
    localStudyInfoTableData: reshapeLocalStudies(state),

    remoteStudiesFetching: state.availableStudies.remoteStudiesFetching,
    remoteStudyInfoTableData: reshapeRemoteStudies(state),
    remoteStudiesReceived: state.availableStudies.remoteStudiesReceived,

    studyLoadDialog: state.utility.dialog.studyLoadDialog,
    localFolderpath: state.availableStudies.localFolderpath,

    setStudyFetching: state.availableStudies.setStudyFetching,

    templateFetching: state.availableStudies.templateFetching,
    templateReceieved: state.availableStudies.templateReceieved,
    templateReceivedInfo: state.availableStudies.templateReceivedInfo,
    templatePrompt: state.availableStudies.templatePrompt,

    theme: state.utility.theme,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LoadStudy);
